import type { LanguageType } from '@/types/language';

/**
 * ### SolutionResourceProduct
 *
 * The product a downloadable resource describes. The members are spelled as
 * the analytics `solution_id` literals, so a download event groups under the
 * same product as the solution-page events without a lookup table in between.
 */
export type SolutionResourceProduct =
  'listen_ai_industrial' | 'listen_ai_safety';

/**
 * ### SolutionResourceFormat
 *
 * How thoroughly a resource covers its product: an `overview` is the full
 * solution introduction deck, a `brochure` the short leave-behind. A product
 * carries at most one file of each format per language.
 */
export type SolutionResourceFormat = 'overview' | 'brochure';

/**
 * ### SolutionResource
 *
 * One downloadable solution document, described once for every screen that
 * offers it. GCS exposes a stable ASCII alias for each language, and nothing
 * outside this module builds that storage URL itself.
 */
export type SolutionResource = {
  /**
   * ### id
   *
   * Slug that identifies the resource in the site's own download path and in
   * analytics. It names the product and the format rather than the file, so a
   * re-exported or renamed file keeps the id it already had.
   */
  id: string;

  /**
   * ### product
   *
   * Product the document describes, used to group the resource page and to
   * report the download under a solution.
   */
  product: SolutionResourceProduct;

  /**
   * ### format
   *
   * Which of the two documents this is; see {@link SolutionResourceFormat}.
   */
  format: SolutionResourceFormat;

  /**
   * ### titleKey
   *
   * Translation key of the title shown to the reader. The Korean phrase is the
   * key itself, matching the rest of the `common` dictionary.
   */
  titleKey: string;

  /**
   * ### fileName
   *
   * File name hosted under {@link SOLUTION_RESOURCE_FILE_BASE_URL}, per site
   * language. The two languages are separate documents rather than
   * translations of one file, so neither name can be derived from the other.
   */
  fileName: Record<LanguageType, string>;
};

/**
 * ### SOLUTION_RESOURCE_FILE_BASE_URL
 *
 * Public GCS prefix that serves the resource files. The objects use stable
 * ASCII aliases so browser URLs do not depend on Unicode normalization or
 * bracket escaping. Pages still publish {@link
 * buildSolutionResourceDownloadPath}, keeping the storage provider out of
 * user-facing links.
 */
export const SOLUTION_RESOURCE_FILE_BASE_URL =
  'https://storage.googleapis.com/deeply-website-media/deeply/resources/';

/**
 * ### SOLUTION_RESOURCE_DOWNLOAD_BASE_PATH
 *
 * Root of the site-owned download path. The language follows as its own
 * segment because the two languages are different files.
 */
export const SOLUTION_RESOURCE_DOWNLOAD_BASE_PATH = '/resources';

/**
 * ### SOLUTION_RESOURCE_PRODUCT_NAMES
 *
 * Product names as they are printed beside a resource. They are product
 * names, identical in every site language, so they are not translation keys.
 */
export const SOLUTION_RESOURCE_PRODUCT_NAMES: Record<
  SolutionResourceProduct,
  string
> = {
  listen_ai_industrial: 'Listen AI Industrial',
  listen_ai_safety: 'Listen AI Safety',
};

/**
 * ### SOLUTION_RESOURCE_PRODUCT_ORDER
 *
 * Order the resource page lists the products in. Industrial comes first, the
 * order the home page and the press kit already lead with.
 */
export const SOLUTION_RESOURCE_PRODUCT_ORDER: ReadonlyArray<SolutionResourceProduct> =
  ['listen_ai_industrial', 'listen_ai_safety'];

/**
 * ### SOLUTION_RESOURCE_FORMAT_LABEL_KEYS
 *
 * Translation key of the word a screen prints for each format, so the two
 * screens that list resources cannot end up naming the same format
 * differently.
 */
export const SOLUTION_RESOURCE_FORMAT_LABEL_KEYS: Record<
  SolutionResourceFormat,
  string
> = {
  overview: '소개서',
  brochure: '브로셔',
};

/**
 * ### SOLUTION_RESOURCES
 *
 * Every resource the site offers, in the order the solution components page
 * has listed them since it launched: both overviews, then both brochures. The
 * resource page regroups them by product rather than relying on this order.
 */
export const SOLUTION_RESOURCES: ReadonlyArray<SolutionResource> = [
  {
    id: 'listen-ai-industrial-overview',
    product: 'listen_ai_industrial',
    format: 'overview',
    titleKey: '기계 품질 관리 솔루션 소개서',
    fileName: {
      ko: 'listen-ai-industrial-overview-ko.pdf',
      en: 'listen-ai-industrial-overview-en.pdf',
    },
  },
  {
    id: 'listen-ai-safety-overview',
    product: 'listen_ai_safety',
    format: 'overview',
    titleKey: '위험상황 감지 솔루션 소개서',
    fileName: {
      ko: 'listen-ai-safety-overview-ko.pdf',
      en: 'listen-ai-safety-overview-en.pdf',
    },
  },
  {
    id: 'listen-ai-industrial-brochure',
    product: 'listen_ai_industrial',
    format: 'brochure',
    titleKey: '기계 품질 관리 솔루션 브로셔',
    fileName: {
      ko: 'listen-ai-industrial-brochure-ko.pdf',
      en: 'listen-ai-industrial-brochure-en.pdf',
    },
  },
  {
    id: 'listen-ai-safety-brochure',
    product: 'listen_ai_safety',
    format: 'brochure',
    titleKey: '위험상황 감지 솔루션 브로셔',
    fileName: {
      ko: 'listen-ai-safety-brochure-ko.pdf',
      en: 'listen-ai-safety-brochure-en.pdf',
    },
  },
];

/**
 * ### buildSolutionResourceFileUrl
 *
 * Builds the address of the hosted file itself. Only the download route and
 * analytics should need it; a link rendered on a page points at {@link
 * buildSolutionResourceDownloadPath} instead, so the file name never reaches
 * a published URL.
 *
 * @param resource Resource whose file is wanted
 * @param language Site language the reader is browsing in
 * @returns Absolute URL of the hosted file for that language
 */
export function buildSolutionResourceFileUrl(
  resource: SolutionResource,
  language: LanguageType,
): string {
  return `${SOLUTION_RESOURCE_FILE_BASE_URL}${resource.fileName[language]}`;
}

/**
 * ### buildSolutionResourceDownloadPath
 *
 * Builds the site-owned path that redirects to the hosted file. The path is
 * derived from the resource id and the language alone, so it survives a file
 * rename and stays valid in a link someone saved or shared.
 *
 * @param resource Resource to link to
 * @param language Site language the reader is browsing in
 * @returns Site-relative download path, without a locale prefix
 */
export function buildSolutionResourceDownloadPath(
  resource: SolutionResource,
  language: LanguageType,
): string {
  return `${SOLUTION_RESOURCE_DOWNLOAD_BASE_PATH}/${language}/${resource.id}`;
}

/**
 * ### findSolutionResource
 *
 * Looks a resource up by the id carried in a download path.
 *
 * @param id Candidate id, usually read straight from the URL
 * @returns The resource, or `undefined` when no resource carries that id
 */
export function findSolutionResource(id: string): SolutionResource | undefined {
  return SOLUTION_RESOURCES.find((resource) => resource.id === id);
}

/**
 * ### getSolutionResourcesForProduct
 *
 * Selects the resources of one product, keeping the order of {@link
 * SOLUTION_RESOURCES} so the overview precedes the brochure.
 *
 * @param product Product to list
 * @returns That product's resources, overview first
 */
export function getSolutionResourcesForProduct(
  product: SolutionResourceProduct,
): ReadonlyArray<SolutionResource> {
  return SOLUTION_RESOURCES.filter((resource) => resource.product === product);
}
